import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { Message } from '../types';
import { getRenderer } from '../utils/renderers';
import type { AnimationState } from '../utils/renderers';

/**
 * Hook to manage message animation states
 * Returns animation states and a flag indicating if any animation is still running
 */
export function useMessageAnimation(messages: Message[]) {
  const [animationStates, setAnimationStates] = useState<
    Map<string, AnimationState>
  >(new Map());

  // Track which messages have completed their animations
  const [completedAnimations, setCompletedAnimations] = useState<Set<string>>(
    new Set()
  );

  // Use ref to track current states without causing re-renders
  const statesRef = useRef<Map<string, AnimationState>>(new Map());
  const completedRef = useRef<Set<string>>(new Set());

  // Sync refs with state
  useEffect(() => {
    statesRef.current = animationStates;
  }, [animationStates]);

  useEffect(() => {
    completedRef.current = completedAnimations;
  }, [completedAnimations]);

  // Check if any animation is still running
  const isAnimating = useMemo(() => {
    return messages.some((message) => {
      if (!message.animation || message.animation.type === 'none') {
        return false;
      }

      const isCompleted = completedAnimations.has(message.id);
      return !isCompleted;
    });
  }, [messages, completedAnimations]);

  // Initialize animation states for new messages
  useEffect(() => {
    setAnimationStates((prev) => {
      const newMap = new Map(prev);
      let hasChanges = false;

      for (const message of messages) {
        if (message.animation && message.animation.type !== 'none') {
          if (!newMap.has(message.id)) {
            const renderer = getRenderer(message.animation.type);
            newMap.set(message.id, renderer.getInitialState());
            hasChanges = true;
          }
        }
      }

      return hasChanges ? newMap : prev;
    });
  }, [messages]);

  // Animate messages using recursive approach per message
  useEffect(() => {
    const timers = new Map<string, ReturnType<typeof setTimeout>>();
    const activeAnimations = new Set<string>();

    // Helper function to calculate next animation state
    const calculateNextState = (
      currentState: AnimationState,
      animationType: string,
      animation: Message['animation'],
      adjustedDelay: number
    ): AnimationState => {
      switch (animationType) {
        case 'line-by-line':
          return {
            visibleLines: (currentState.visibleLines ?? 0) + 1,
          };
        case 'typewriter': {
          const charDelay = animation?.config?.charDelay ?? 30;
          const charsToAdd = Math.max(1, Math.round(charDelay / 30));
          return {
            visibleChars: (currentState.visibleChars ?? 0) + charsToAdd,
          };
        }
        case 'fade-in':
          return {
            fadeOpacity: Math.min(1, (currentState.fadeOpacity ?? 0) + 0.1),
          };
        case 'progress': {
          const steps = animation?.config?.progressSteps ?? [];
          const currentStep = currentState.currentStep ?? 0;
          const stepProgress = (currentState.stepProgress ?? 0) + adjustedDelay;

          if (currentStep < steps.length) {
            const step = steps[currentStep];
            if (stepProgress >= step.duration) {
              return {
                currentStep: currentStep + 1,
                stepProgress: 0,
              };
            }
            return {
              currentStep,
              stepProgress,
            };
          }
          return currentState;
        }
        default:
          return currentState;
      }
    };

    // Helper function to handle animation completion
    const handleAnimationComplete = (messageId: string) => {
      activeAnimations.delete(messageId);
      timers.delete(messageId);
      if (!completedRef.current.has(messageId)) {
        setCompletedAnimations((prevCompleted) => {
          const newSet = new Set(prevCompleted);
          newSet.add(messageId);
          return newSet;
        });
      }
    };

    // Helper function to schedule next animation step
    const scheduleNextStep = (
      messageId: string,
      animateStep: () => void,
      adjustedDelay: number
    ) => {
      const timer = setTimeout(() => {
        animateStep();
      }, adjustedDelay);
      timers.set(messageId, timer);
    };

    // Helper function to process animation step state update
    const processAnimationStep = (
      message: Message,
      renderer: ReturnType<typeof getRenderer>,
      animation: NonNullable<Message['animation']>,
      delay: number,
      adjustedDelay: number,
      animateStep: () => void
    ) => {
      return (
        prev: Map<string, AnimationState>
      ): Map<string, AnimationState> => {
        const currentState = prev.get(message.id) ?? renderer.getInitialState();

        // Check current state
        const result = renderer.render(
          message.id,
          {
            content: message.content,
            delay,
            speed: animation.speed ?? 1,
            config: animation.config,
          },
          currentState
        );

        // Mark as completed if animation is done
        if (result.isComplete) {
          handleAnimationComplete(message.id);
          return prev; // No state change needed
        }

        // Calculate next state
        const updatedState = calculateNextState(
          currentState,
          animation.type,
          animation,
          adjustedDelay
        );

        const newState = new Map(prev);
        newState.set(message.id, updatedState);

        // Schedule next animation step after state is updated
        scheduleNextStep(message.id, animateStep, adjustedDelay);

        return newState;
      };
    };

    const animateMessage = (message: Message) => {
      if (!message.animation || message.animation.type === 'none') {
        return;
      }

      // Skip if already completed or already animating
      if (
        completedRef.current.has(message.id) ||
        activeAnimations.has(message.id)
      ) {
        return;
      }

      activeAnimations.add(message.id);

      // Store animation in local variable for type narrowing
      const animation = message.animation;
      const renderer = getRenderer(animation.type);
      const delay = animation.delay ?? 200;
      const adjustedDelay = Math.round(delay / (animation.speed ?? 1));

      const animateStep = () => {
        // Clear existing timer for this message
        const existingTimer = timers.get(message.id);
        if (existingTimer) {
          clearTimeout(existingTimer);
        }

        // Check if still valid
        if (completedRef.current.has(message.id)) {
          activeAnimations.delete(message.id);
          timers.delete(message.id);
          return;
        }

        // Update state using functional update to get current state
        setAnimationStates(
          processAnimationStep(
            message,
            renderer,
            animation,
            delay,
            adjustedDelay,
            animateStep
          )
        );
      };

      // Start animation
      animateStep();
    };

    // Start animation for each message
    for (const message of messages) {
      animateMessage(message);
    }

    return () => {
      for (const timer of timers.values()) {
        clearTimeout(timer);
      }
      timers.clear();
      activeAnimations.clear();
    };
  }, [messages]);

  // Get render result for a specific message
  const getRenderResult = useCallback(
    (message: Message) => {
      if (!message.animation || message.animation.type === 'none') {
        return {
          displayContent: message.content,
          isComplete: true,
        };
      }

      const renderer = getRenderer(message.animation.type);
      const currentState =
        animationStates.get(message.id) ?? renderer.getInitialState();

      return renderer.render(
        message.id,
        {
          content: message.content,
          delay: message.animation?.delay ?? 200,
          speed: message.animation?.speed ?? 1,
          config: message.animation?.config,
        },
        currentState
      );
    },
    [animationStates]
  );

  return {
    animationStates,
    isAnimating,
    getRenderResult,
  };
}
